/* =====================================================================================
   Per-method vendor, product and the date the account is free to switch.

   "Runs Luminex" tells you nothing about whether you can sell to them. The vendor, the
   product and the contract end date do. A laboratory delighted with your assay and locked
   into a framework until 2029 is not a prospect this year; it is a diary entry for 2028, and
   the difference is the whole of commercial sequencing.

   ADDRESSABLE FROM is computed in the database as the end date less the notice period, and is
   shown in preference to the raw expiry, because the expiry is not the date anything can
   actually be done.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchMethodVendors, saveMethodVendor, METHODS, COMMITMENTS, type MethodVendor,
} from '../lib/technical';
import { onFail } from '../lib/errors';
import { useDialog } from '../lib/ui';

const label = (k: string) => METHODS.find((m) => m.key === k)?.label ?? k;

function windowOf(v: MethodVendor): { text: string; tone: string } {
  if (!v.ends_on) return { text: 'no end date recorded', tone: 'mute' };
  const from = v.addressable_from ?? v.ends_on;
  const days = Math.round((new Date(from).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return { text: 'addressable now', tone: 'ok' };
  if (days <= 180) return { text: `opens in ${days} days`, tone: 'accent' };
  if (days <= 365) return { text: `opens in ${Math.round(days / 30)} months`, tone: 'info' };
  return { text: `locked until ${from}`, tone: 'mute' };
}

export function MethodVendors({ siteId }: { siteId: string }) {
  const [rows, setRows] = useState<MethodVendor[]>([]);
  const [editing, setEditing] = useState<Partial<MethodVendor> | null>(null);

  const reload = useCallback(() => {
    fetchMethodVendors(siteId).then(setRows).catch(onFail('Vendor records', () => setRows([])));
  }, [siteId]);
  useEffect(reload, [reload]);

  return (
    <section className="tech-section">
      <div className="tech-head">
        <h3>Vendors and commitments</h3>
        <button className="btn-compose" onClick={() => setEditing({ method: 'hla_typing_high' })}>
          Add a method
        </button>
      </div>

      {!rows.length && (
        <p className="module-empty">
          Nothing recorded. Who supplies each method, and when the contract ends, is what
          decides whether this account can be approached now or in three years.
        </p>
      )}

      {rows.length > 0 && (
        <table className="dr-table">
          <thead>
            <tr>
              <th>Method</th><th>Vendor</th><th>Product</th><th>Commitment</th>
              <th>Ends</th><th>Addressable</th><th>Fit</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => {
              const w = windowOf(v);
              return (
                <tr key={v.id}>
                  <td>{label(v.method)}</td>
                  <td><strong>{v.vendor ?? '—'}</strong></td>
                  <td className="muted">{v.product ?? '—'}</td>
                  <td className="muted">{v.commitment ?? '—'}{v.commitment_ref ? ` · ${v.commitment_ref}` : ''}</td>
                  <td>{v.ends_on ?? '—'}</td>
                  <td><span className={`pill ${w.tone}`}>{w.text}</span></td>
                  <td className="muted">
                    {v.satisfaction ?? '—'}
                    {v.displaceable === 'yes' && <span className="pill ok">displaceable</span>}
                  </td>
                  <td><button className="link-btn" onClick={() => setEditing(v)}>Edit</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing && (
        <VendorDialog siteId={siteId} row={editing}
                      onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); reload(); }} />
      )}
    </section>
  );
}

function VendorDialog({ siteId, row, onClose, onSaved }: {
  siteId: string; row: Partial<MethodVendor>; onClose: () => void; onSaved: () => void;
}) {
  const ref = useDialog(onClose);
  const [f, setF] = useState<Partial<MethodVendor>>(row);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try { await saveMethodVendor(siteId, f); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  const set = (k: keyof MethodVendor) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value || null });

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Method vendor" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>Method vendor and commitment</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          <label className="cf-field"><span>Method</span>
            <select value={f.method ?? ''} onChange={set('method')}>
              {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </label>
          <div className="cf-row">
            <label className="cf-field"><span>Vendor</span>
              <input value={f.vendor ?? ''} onChange={set('vendor')} placeholder="One Lambda" /></label>
            <label className="cf-field"><span>Product</span>
              <input value={f.product ?? ''} onChange={set('product')} placeholder="LABScreen" /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>Commitment</span>
              <select value={f.commitment ?? ''} onChange={set('commitment')}>
                <option value="">Not known</option>
                {COMMITMENTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="cf-field"><span>Reference</span>
              <input value={f.commitment_ref ?? ''} onChange={set('commitment_ref')} /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>Starts</span>
              <input type="date" value={f.starts_on ?? ''} onChange={set('starts_on')} /></label>
            <label className="cf-field"><span>Ends</span>
              <input type="date" value={f.ends_on ?? ''} onChange={set('ends_on')} /></label>
            <label className="cf-field"><span>Notice (months)</span>
              <input type="number" min={0} value={f.notice_months ?? ''}
                     onChange={(e) => setF({ ...f, notice_months: e.target.value ? Number(e.target.value) : null })} /></label>
          </div>
          <p className="cf-note">
            The date this account becomes addressable is computed as the end date less the
            notice period. That, not the expiry, is what Pipeline sequences on.
          </p>
          <div className="cf-row">
            <label className="cf-field"><span>How well is it working</span>
              <select value={f.satisfaction ?? ''} onChange={set('satisfaction')}>
                <option value="">Not known</option>
                {['poor', 'mixed', 'good', 'excellent', 'unknown'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
            <label className="cf-field"><span>Displaceable</span>
              <select value={f.displaceable ?? ''} onChange={set('displaceable')}>
                <option value="">Not known</option>
                {['yes', 'unlikely', 'no', 'unknown'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
          </div>
          <label className="cf-field"><span>Notes</span>
            <textarea rows={2} value={f.notes ?? ''} onChange={set('notes')} /></label>

          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" onClick={save} disabled={busy || !f.method}>
              {busy ? 'Saving' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
